import { Helmet } from "react-helmet";
import {
    getBreadcrumbsByUriV2,
    getPageByUriV2,
    getAllPagesV2,
} from "lib/pages";
import { WebpageJsonLd } from "lib/json-ld";
import { getSiteData, helmetSettingsFromMetadata } from "lib/site";
import useSite, { SiteContext, useSiteContext } from "hooks/use-site";
import usePageMetadata from "hooks/use-page-metadata";

import Layout from "components/Layout";
import Loading from "components/Loading";
import SectionContent from "components/SectionContent";
import SectionText from "components/SectionText";
import SectionTextProductServiceV2 from "components/SectionTextProductServiceV2";
import SectionBackgroundProductServiceV2 from "components/SectionBackgroundProductServiceV2";
import SectionContentProductServiceV2 from "components/SectionContentProductServiceV2";
import SectionBackground from "components/SectionBackground";
import RenderFromTemplate from "components/RenderFromTemplate.js";
import useMedia from "hooks/use-media";
import Breadcrumbs from "components/Breadcrumbs";
import Container from "components/Container";
import styles from "styles/pages/Page.module.scss";
import { abort } from "lib/abort";
import Error from "components/Error";
import { getShowUi } from "data/fetchPlugin";

const sectionStyles = {
    display: "flex",
    paddingTop: "12rem",
    paddingBottom: "1rem",
    minHeight: "75vh",
};
export default function Page(props) {
    if (props?.hasError) {
        return <Error statusCode={props.code} />;
    }
    if (props?.page) {
        const isSm = useMedia("(max-width: 480px)");
        sectionStyles.minHeight = isSm ? "57vh" : "75vh";
        sectionStyles.paddingTop = isSm ? "10rem" : "12rem";
        const { page, pagination, breadcrumbs, years, options = {} } = props;
        const { title, description, slug, templateType, headerContent } = page;
        const { metadata: siteMetadata = {} } = useSite();
        const { metadata } = usePageMetadata({
            metadata: {
                ...page,
                description:
                    description ||
                    page.og?.description ||
                    `Read more about ${title}`,
            },
        });
        if (process.env.WORDPRESS_PLUGIN_SEO !== true) {
            metadata.title = `${title} - ${siteMetadata.title}`;
            metadata.og.title = metadata.title;
            metadata.twitter.title = metadata.title;
        }
        const helmetSettings = helmetSettingsFromMetadata(metadata);
        const site = useSiteContext({
            siteData: props.siteData,
            showUi: props.showUi,
            locale: props.locale,
            relativePath: props.relativePath,
        });
        const isNoHeader = [
            "Template_NoHeader",
            "template-no-header",
            "No - Header",
        ].includes(templateType);
        return (
            <SiteContext.Provider value={site}>
                <Layout
                    isFullColor={isNoHeader ? true : false}
                    templateType={templateType}>
                    <Helmet {...helmetSettings} />
                    <WebpageJsonLd
                        title={metadata.title}
                        description={metadata.description}
                        siteTitle={siteMetadata.title}
                        slug={slug}
                    />
                    {!isNoHeader &&
                        ([
                            "template-product-services-v2",
                            "Template_ProductServicesV2",
                        ].includes(templateType) ? (
                            <SectionContentProductServiceV2>
                                <SectionTextProductServiceV2 />
                                <SectionBackgroundProductServiceV2 />
                            </SectionContentProductServiceV2>
                        ) : (
                            <SectionContent style={sectionStyles}>
                                <SectionText
                                    section="top-content"
                                    pageTemplate={templateType}
                                    content={headerContent}
                                />
                                <SectionBackground
                                    imageSrc={templateType}
                                    srcUrl={
                                        headerContent?.banner?.sourceUrl || null
                                    }
                                />
                            </SectionContent>
                        ))}
                    {/* remove breadcrumb */}
                    {![
                        "Template_DownloadFilesV3",
                        "template-download-v3",
                        "template-product-services-v2",
                        "Template_ProductServicesV2",
                    ].includes(templateType) && (
                        <Container className={styles.breadcrumbWrapper}>
                            <Breadcrumbs breadcrumbs={breadcrumbs} />
                        </Container>
                    )}
                    <RenderFromTemplate
                        isNoHeader={isNoHeader}
                        template={templateType}
                        page={page}
                        pagination={pagination}
                        years={years}
                        options={options}
                    />
                </Layout>
            </SiteContext.Provider>
        );
    } else {
        return <Loading />;
    }
}

export async function getStaticProps({ params = {}, locale, query } = {}) {
    const { slugParent, slugChild } = params;
    let pageUri = `/${slugParent}/`;
    if (Array.isArray(slugChild) && slugChild.length > 0) {
        pageUri = `${pageUri}${slugChild.join("/")}/`;
    }

    const year = query?.year ? Number(query?.year) : null;
    const kw = query?.kw ? query?.kw : null;
    const currentPage = query?.page ? query.page : 1;
    let { page, years } = await getPageByUriV2(
        pageUri,
        locale,
        currentPage,
        year,
        kw,
    );

    if (page?.code === "not_found" && page?.data?.status === 404) {
        return abort(404);
    }

    const breadcrumbs = getBreadcrumbsByUriV2(pageUri, locale.toUpperCase());
    const basePath = locale === "th" ? `/th/${slugParent}` : `/${slugParent}`;
    let paginationObj = { ...page.pagination, basePath };
    if (year) {
        paginationObj = { ...paginationObj, currentYear: year };
    }
    if (kw) {
        paginationObj = { ...paginationObj, kw };
    }
    const siteData = await getSiteData();
    const showUi = await getShowUi();
    return {
        props: {
            page,
            breadcrumbs,
            pagination: paginationObj,
            years,
            options: page?.options || {},
            locale,
            showUi,
            siteData,
            relativePath: page?.relative_path || null,
        },
    };
}

export async function getStaticPaths({ locales }) {
    const paths = [];
    for (const locale of locales) {
        const { pages } = await getAllPagesV2(locale);
        pages
            .filter(({ uri }) => typeof uri === "string")
            .map(({ uri, slug }) => {
                const segments = uri.split("/").filter(seg => seg !== "");
                paths.push({
                    params: {
                        slugParent: segments.shift(),
                        slugChild: segments,
                    },
                    locale,
                });
            });
    }
    return { paths, fallback: "blocking" };
}
